import { DataGapsCallout } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/DataGapsCallout.tsx';
import { DetailField } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/DetailField.tsx';
import { RiskFactorsCallout } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/RiskFactorsCallout.tsx';
import { ValidatedFieldsSection } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/BankAccounts/BankAccountDetails/components/ValidatedFieldsSection.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	useTranslationCommonUi,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { CLASSIC_IDENTIFIER_KEYS } from '@dealstack/shared/lib/common/business/bankAccountIdentifiers';
import { MipInvestorBankAccountDetailRes } from '@dealstack/shared/lib/generated';
import { REPLAY_MASK_CLASS } from '@dealstack/frontend/shared/sentryPrivacy';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Button, Divider, Flex, SimpleGrid, Text } from '@mantine/core';

interface Props {
	bankAccount: MipInvestorBankAccountDetailRes;
	/** Omitted when the account cannot be edited from here, e.g. a programme account. */
	onEdit?: () => void;
}

export const BankAccountFieldsCard = ({ bankAccount, onEdit }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();

	// Identifiers the validated section does not already cover (sort code, routing number, ...).
	const extraIdentifiers = bankAccount.identifiers.filter(
		(identifier) => !CLASSIC_IDENTIFIER_KEYS.has(identifier.key)
	);

	return (
		<Flex direction="column" gap="1rem" p="1rem">
			<Flex justify="space-between" align="center" gap="1rem">
				<Text size="md" fw={600} c="dark.8">
					{tMipManage('bank_accounts.details.card_title')}
				</Text>

				{onEdit && (
					<Button
						variant="subtle"
						onClick={onEdit}
						leftSection={<Icon variant="pencil" size="lg" />}
						data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_EDIT_BUTTON}
					>
						{tCommonUi('common_buttons.edit')}
					</Button>
				)}
			</Flex>

			{/* Unlike the list, the detail page shows identifiers in full. */}
			<SimpleGrid
				cols={{ base: 1, sm: 2, lg: 4 }}
				spacing="1.5rem"
				verticalSpacing="1rem"
				className={REPLAY_MASK_CLASS}
				data-sentry-mask
			>
				<DetailField
					label={tMipManage('bank_accounts.table.account_holder')}
					value={bankAccount.accountHolder}
				/>
				{!!bankAccount.iban && (
					<DetailField
						label={tMipManage('bank_accounts.details.iban')}
						value={bankAccount.iban}
					/>
				)}
				{!!bankAccount.accountNumber && (
					<DetailField
						label={tMipManage('bank_accounts.details.account_number')}
						value={bankAccount.accountNumber}
					/>
				)}
				{extraIdentifiers.map((identifier) => (
					<DetailField
						key={identifier.key}
						label={identifier.label}
						value={identifier.value}
					/>
				))}
			</SimpleGrid>

			<ValidatedFieldsSection bankAccount={bankAccount} />

			{(bankAccount.dataGaps.length > 0 || bankAccount.riskFactors.length > 0) && (
				<>
					<Divider />

					<Flex maw="593px" direction="column" gap="1rem">
						<DataGapsCallout dataGaps={bankAccount.dataGaps} />
						<RiskFactorsCallout riskFactors={bankAccount.riskFactors} />
					</Flex>
				</>
			)}
		</Flex>
	);
};
